import * as React from 'react';
import EntityGroupHeader, { EntityGroup } from './extensibleEntity/EntityGroupHeader';
import { Dictionary } from '../../entities/common';
import { ISuggestedSubentity } from '../../entities/Subentities';
import { ListGroupingProps } from './extensibleEntity/EntityDetailsList';
import { Grouping, IRow } from './timeline/TimelineList';
import { ITimelineProps } from './extensibleEntity/EntityTimelineList';
import { ITimelineMapProps } from './extensibleEntity/EntityTimelineMap';

export class SuggestedHelper {

    readonly ITEMS_GROUP_KEY = "ppmx_entity_items";
    readonly rowType = "group";
    
    private readonly _suggestedEntities?: ISuggestedSubentity[];
    private readonly _entityPluralLabel: string;

    public constructor(entityPluralLabel: string, suggestedEntities?: ISuggestedSubentity[]) {
        this._suggestedEntities = suggestedEntities;
        this._entityPluralLabel = entityPluralLabel;
    }

    public getGroups(groups: EntityGroup[] | undefined, entitiesLength: number): EntityGroup[] | undefined {
        if (!this._suggestedEntities?.length) {
            return groups;
        }

        const groupsMap = this._suggestedEntities.reduce<Dictionary<EntityGroup>>((acc, item) => {
            const suggestedBy = acc[item.suggestedBy];
            if (!suggestedBy) {
                acc[item.suggestedBy] = {
                    key: item.suggestedBy,
                    name: `Suggested by ${item.suggestedBy}`,
                    title: `${this._entityPluralLabel} suggested by ${item.suggestedBy}`,
                    color: "#6446CF",
                    count: 1,
                    data: { isSuggested: true }
                }
            } else {
                suggestedBy.count++
            }
            return acc;
        }, {});

        const groupValues = Object.values(groupsMap);
        return groups
            ? groupValues.concat(groups)
            : groupValues.concat([{ key: this.ITEMS_GROUP_KEY, name: this._entityPluralLabel, color: "#6a6b6d", count: entitiesLength, data: { isSuggested: false } }]);
    }

    private _renderGroupHeaderDefault = (
        group: EntityGroup,
        isCollapsed: boolean,
        isSelected: boolean | undefined,
        onToggleCollapse: (e: any) => void,
        onToggleSelect: (e: any) => void): JSX.Element | null => <EntityGroupHeader
            group={group}
            isCollapsed={isCollapsed}
            isSelected={isSelected}
            onToggleCollapse={onToggleCollapse}
            onToggleSelect={onToggleSelect}
        />

    public getListGrouping(pngExportDetails: boolean, propsListGrouping?: ListGroupingProps): ListGroupingProps | undefined {
        const listGrouping = pngExportDetails && propsListGrouping ? { ...propsListGrouping, renderGroupFooter: undefined } : propsListGrouping;
        return this._suggestedEntities?.length
            ? {
                ...listGrouping ?? {},
                getGroupKey: (item: ISuggestedSubentity) => item.isSuggested
                    ? item.suggestedBy
                    : listGrouping?.getGroupKey!(item) ?? this.ITEMS_GROUP_KEY,
                renderGroupHeader: listGrouping?.renderGroupHeader ?? this._renderGroupHeaderDefault
            }
            : listGrouping;
    }

    private _getListViewGroupRow(group: EntityGroup): IRow {
        const entity = {
            id: '',
            attributes: {},
            externalData: {}
        };

        return {
            key: group.key,
            rowType: this.rowType,
            entity,
            segments: [],
            markers: []
        }
    }

    private _getTimelineMapGroupRow(group: EntityGroup): IRow {
        const entity = {
            id: '',
            attributes: {
                Lane: { id: group.key, name: group.name, color: group.color }
            },
            externalData: {
            }
        };

        return {
            key: group.key,
            rowType: this.rowType,
            entity,
            segments: [],
            markers: []
        }
    }

    private _buildTimelineMapGrouping = (): Grouping => ({
        getGroupRow: (group: EntityGroup, rows: IRow[]): IRow => this._getTimelineMapGroupRow(group),
        getGroupKey: (item: ISuggestedSubentity) => item.isSuggested
            ? item.suggestedBy
            : this.ITEMS_GROUP_KEY,
        renderGroupHeader: this._renderGroupHeaderDefault
    });

    public getTimelineProps(propsTimelineProps?: ITimelineProps): ITimelineProps | undefined {
        if (!propsTimelineProps || !this._suggestedEntities?.length || propsTimelineProps.timelineGrouping) {
            return propsTimelineProps;
        }
        return {
            ...propsTimelineProps, timelineGrouping: {
                getGroupRow: this._getListViewGroupRow
            }
        };
    }

    public getTimelineMapProps(propsTimelineMapProps?: ITimelineMapProps): ITimelineMapProps | undefined {
        if (!propsTimelineMapProps || !this._suggestedEntities?.length || propsTimelineMapProps.grouping) {
            return propsTimelineMapProps;
        }
        return {
            ...propsTimelineMapProps, grouping: this._buildTimelineMapGrouping()
        };
    }
}